'use client';

import React, { useEffect, useRef, useState } from 'react';
import { useLang } from './LanguageContext';

export default function ContactSection() {
  const { t } = useLang();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', dest: '' });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.08 }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // TODO: Connect to backend/email service
    setSubmitted(true);
  };

  const titleLines = t('contact_title').split('\n');

  return (
    <section id="contact" ref={sectionRef} className="py-24 md:py-32 relative">
      {/* Top divider */}
      <div
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(201,168,76,0.25), transparent)' }}
      />

      <div className="max-w-8xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          {/* Left — info */}
          <div>
            <div className="section-label mb-4 reveal">{t('contact_label')}</div>
            <h2
              className="font-serif font-bold text-cream mb-6 reveal reveal-delay-1"
              style={{
                fontFamily: 'Fraunces, serif',
                fontSize: 'clamp(2rem, 5vw, 3.8rem)',
                lineHeight: 1.1,
              }}
            >
              {titleLines.map((line, i) => (
                <span key={i} className="block">
                  {i === 1 ? <span className="text-shimmer">{line}</span> : line}
                </span>
              ))}
            </h2>
            <p className="text-muted text-base leading-relaxed mb-10 max-w-md reveal reveal-delay-2">
              {t('contact_subtitle')}
            </p>

            {/* Contact links */}
            <div className="flex flex-col gap-4 reveal reveal-delay-3">
              {/* Phone */}
              <a href="tel:+998200101040" className="contact-link">
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 text-gold"
                  style={{ background: 'rgba(201,168,76,0.12)' }}
                >
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.4 2 2 0 0 1 3.6 1.21h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.82A16 16 0 0 0 15.1 16l.98-.98a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
                  </svg>
                </div>
                <div>
                  <div className="text-cream/50 text-xs tracking-wider uppercase mb-0.5">{t('contact_phone')}</div>
                  <div className="text-cream font-semibold text-lg tracking-wide">+998 20 010 10 40</div>
                </div>
              </a>

              {/* Telegram */}
              <a
                href="https://t.me/AIR_DUBAY"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-link"
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(0,136,204,0.15)' }}
                >
                  {/* Telegram SVG */}
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <path
                      d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8l-1.68 7.9c-.12.56-.46.7-.94.43l-2.6-1.92-1.25 1.2c-.14.14-.26.26-.53.26l.19-2.65 4.83-4.37c.21-.19-.05-.29-.32-.1l-5.97 3.76-2.57-.8c-.56-.17-.57-.56.12-.83l10.03-3.87c.46-.17.87.11.69.99z"
                      fill="#0088CC"
                    />
                  </svg>
                </div>
                <div>
                  <div className="text-cream/50 text-xs tracking-wider uppercase mb-0.5">{t('contact_telegram')}</div>
                  <div className="font-semibold text-base" style={{ color: '#0088CC' }}>@AIR_DUBAY</div>
                </div>
              </a>

              {/* Instagram */}
              <a
                href="https://instagram.com/AIR_DUBAY"
                target="_blank"
                rel="noopener noreferrer"
                className="contact-link"
              >
                <div
                  className="w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'rgba(225,48,108,0.12)' }}
                >
                  {/* Instagram gradient icon */}
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                    <defs>
                      <linearGradient id="igGrad" x1="0" y1="1" x2="1" y2="0">
                        <stop offset="0%" stopColor="#F58529"/>
                        <stop offset="50%" stopColor="#DD2A7B"/>
                        <stop offset="100%" stopColor="#8134AF"/>
                      </linearGradient>
                    </defs>
                    <rect x="2" y="2" width="20" height="20" rx="5" stroke="url(#igGrad)" strokeWidth="2"/>
                    <circle cx="12" cy="12" r="5" stroke="url(#igGrad)" strokeWidth="2"/>
                    <circle cx="17.5" cy="6.5" r="1.5" fill="url(#igGrad)"/>
                  </svg>
                </div>
                <div>
                  <div className="text-cream/50 text-xs tracking-wider uppercase mb-0.5">{t('contact_instagram')}</div>
                  <div
                    className="font-semibold text-base"
                    style={{
                      background: 'linear-gradient(90deg, #F58529, #DD2A7B)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    @AIR_DUBAY
                  </div>
                </div>
              </a>

              {/* Hours */}
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'rgba(201,168,76,0.06)' }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
                </svg>
                <span className="text-cream/60 text-sm">{t('contact_hours')}</span>
              </div>
            </div>
          </div>

          {/* Right — form */}
          <div className="reveal reveal-delay-2">
            <div className="glass rounded-3xl p-8 md:p-10">
              {submitted ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mb-6 animate-pulse-glow"
                    style={{ background: 'rgba(201,168,76,0.2)', border: '2px solid var(--gold)' }}
                  >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth="2.5">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  </div>
                  <h3
                    className="font-serif font-bold text-cream text-2xl mb-3"
                    style={{ fontFamily: 'Fraunces, serif' }}
                  >
                    ✓
                  </h3>
                  <p className="text-muted text-base">{t('contact_form_success')}</p>
                  <button
                    onClick={() => { setSubmitted(false); setFormData({ name: '', phone: '', dest: '' }); }}
                    className="mt-6 text-gold text-sm underline underline-offset-4 hover:text-gold-light transition-colors"
                  >
                    ←
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-col gap-6">
                  {/* Name */}
                  <div className="flex flex-col gap-2">
                    <label className="text-cream/50 text-xs tracking-wider uppercase">{t('contact_form_name')}</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-cream placeholder-cream/20 text-sm focus:outline-none focus:border-gold/50 transition-colors"
                      placeholder="—"
                    />
                  </div>

                  {/* Phone */}
                  <div className="flex flex-col gap-2">
                    <label className="text-cream/50 text-xs tracking-wider uppercase">{t('contact_form_phone')}</label>
                    <input
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-cream placeholder-cream/20 text-sm focus:outline-none focus:border-gold/50 transition-colors"
                      placeholder="+998 __ ___ __ __"
                    />
                  </div>

                  {/* Destination */}
                  <div className="flex flex-col gap-2">
                    <label className="text-cream/50 text-xs tracking-wider uppercase">{t('contact_form_dest')}</label>
                    <select
                      value={formData.dest}
                      onChange={(e) => setFormData({ ...formData, dest: e.target.value })}
                      className="bg-white/5 border border-white/10 rounded-xl px-4 py-3.5 text-cream text-sm focus:outline-none focus:border-gold/50 transition-colors appearance-none"
                      style={{ background: 'rgba(255,255,255,0.05)' }}
                    >
                      <option value="" style={{ background: '#0D1B2A' }}>—</option>
                      <option value="dubai" style={{ background: '#0D1B2A' }}>Dubai 🇦🇪</option>
                      <option value="samarkand" style={{ background: '#0D1B2A' }}>Samarkand 🇺🇿</option>
                      <option value="bukhara" style={{ background: '#0D1B2A' }}>Bukhara 🇺🇿</option>
                      <option value="istanbul" style={{ background: '#0D1B2A' }}>Istanbul 🇹🇷</option>
                      <option value="maldives" style={{ background: '#0D1B2A' }}>Maldives 🇲🇻</option>
                      <option value="paris" style={{ background: '#0D1B2A' }}>Paris 🇫🇷</option>
                    </select>
                  </div>

                  <button type="submit" className="btn-primary justify-center mt-2">
                    {t('contact_form_submit')}
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                    </svg>
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}